import { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

// ─── Shared UI ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{
      background: accent ? "rgba(212,175,55,0.07)" : "var(--bg-card)",
      border: `1px solid ${accent ? "rgba(212,175,55,0.25)" : "#1E2C1E"}`,
      borderRadius: "14px", padding: "20px 24px",
    }}>
      <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "8px" }}>{label}</div>
      <div style={{ fontSize: "28px", fontWeight: "900", color: accent ? "#D4AF37" : "#F5F2EE", letterSpacing: "-0.04em" }}>{value}</div>
      {sub && <div style={{ fontSize: "12px", color: "var(--text-faint)", marginTop: "4px" }}>{sub}</div>}
    </div>
  );
}

function TabBtn({ label, active, onClick, badge }) {
  return (
    <button onClick={onClick} style={{
      padding: "10px 20px", border: "none", borderRadius: "10px", cursor: "pointer",
      background: active ? "rgba(212,175,55,0.12)" : "transparent",
      color: active ? "#D4AF37" : "#777",
      fontWeight: active ? "600" : "400", fontSize: "13px",
      fontFamily: "'Capsule Sans', sans-serif", transition: "all 0.15s",
      borderLeft: active ? "2px solid #D4AF37" : "2px solid transparent",
      display: "flex", alignItems: "center", gap: "8px",
    }}>
      {label}
      {badge != null && (
        <span style={{
          background: "var(--bg-hover)", color: "var(--text-muted)", fontSize: "10px",
          padding: "2px 7px", borderRadius: "10px", fontFamily: "'JetBrains Mono', monospace",
        }}>{badge}</span>
      )}
    </button>
  );
}

function Alert({ type, msg, onClose }) {
  if (!msg) return null;
  const colors = { success: ["#0e2a18", "#1a5a30", "#52D68A"], error: ["#2a1010", "#5a2020", "#e07070"] };
  const [bg, border, text] = colors[type] || colors.error;
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: "10px", padding: "12px 16px", fontSize: "13px", color: text, marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      {msg}
      <button onClick={onClose} style={{ background: "none", border: "none", color: text, cursor: "pointer", fontSize: "16px", lineHeight: 1 }}>×</button>
    </div>
  );
}

// ─── Blog Tab ─────────────────────────────────────────────────────────────────
function BlogTab({ token }) {
  const [posts,   setPosts]   = useState([]);
  const [editing, setEditing] = useState(null); // null = list, "new" = new form, post = edit form
  const [alert,   setAlert]   = useState(null);
  const [saving,  setSaving]  = useState(false);

  const [form, setForm] = useState({ title: "", excerpt: "", content: "", tags: "", published: false });

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/v1/admin/blog`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setPosts(await r.json());
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ title: "", excerpt: "", content: "", tags: "", published: false });
    setEditing("new");
  };

  const openEdit = (post) => {
    setForm({ title: post.title, excerpt: post.excerpt, content: post.content, tags: (post.tags || []).join(", "), published: post.published });
    setEditing(post);
  };

  const save = async () => {
    setSaving(true);
    const body = { ...form, tags: form.tags.split(",").map(t => t.trim()).filter(Boolean) };
    const isNew = editing === "new";
    const url   = isNew ? `${API}/api/v1/admin/blog` : `${API}/api/v1/admin/blog/${editing.id}`;
    const method = isNew ? "POST" : "PUT";
    const r = await fetch(url, { method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
    setSaving(false);
    if (r.ok) { setAlert({ type: "success", msg: isNew ? "Post created!" : "Post updated!" }); setEditing(null); load(); }
    else { const d = await r.json(); setAlert({ type: "error", msg: d.error || "Save failed" }); }
  };

  const del = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    const r = await fetch(`${API}/api/v1/admin/blog/${post.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { setAlert({ type: "success", msg: "Post deleted." }); load(); }
    else setAlert({ type: "error", msg: "Delete failed." });
  };

  const inp = {
    width: "100%", padding: "11px 14px", background: "var(--bg-deepest)", border: "1px solid var(--border-strong)",
    borderRadius: "10px", color: "var(--text-primary)", fontSize: "14px", fontFamily: "'Capsule Sans', sans-serif",
    outline: "none", boxSizing: "border-box",
  };
  const lbl = { display: "block", fontSize: "11px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: "7px" };

  if (editing !== null) return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
        <h2 style={{ fontSize: "20px", fontWeight: "700", color: "var(--text-primary)" }}>{editing === "new" ? "New Post" : "Edit Post"}</h2>
        <button onClick={() => setEditing(null)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "14px" }}>← Back to posts</button>
      </div>
      <Alert type={alert?.type} msg={alert?.msg} onClose={() => setAlert(null)} />
      <div style={{ display: "grid", gap: "18px" }}>
        <div><label style={lbl}>Title</label><input style={inp} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Post title" /></div>
        <div><label style={lbl}>Excerpt</label><textarea style={{ ...inp, minHeight: "80px", resize: "vertical" }} value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} placeholder="Short summary shown in the list view" /></div>
        <div><label style={lbl}>Content (HTML)</label><textarea style={{ ...inp, minHeight: "320px", resize: "vertical", fontFamily: "'JetBrains Mono', monospace", fontSize: "13px" }} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="<h2>Section</h2><p>Your content...</p>" /></div>
        <div><label style={lbl}>Tags (comma-separated)</label><input style={inp} value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="investing, budgeting, savings" /></div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px", color: "var(--text-muted)" }}>
            <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))} style={{ accentColor: "#D4AF37", width: "16px", height: "16px" }} />
            Publish immediately
          </label>
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          <button onClick={save} disabled={saving} style={{ padding: "12px 28px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", border: "none", borderRadius: "10px", color: "#121A12", fontWeight: "700", fontSize: "14px", cursor: "pointer", opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save Post"}</button>
          <button onClick={() => setEditing(null)} style={{ padding: "12px 20px", background: "transparent", border: "1px solid var(--border-strong)", borderRadius: "10px", color: "var(--text-muted)", fontSize: "14px", cursor: "pointer" }}>Cancel</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
        <div>
          <h2 style={{ fontSize: "20px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "4px" }}>Blog Posts</h2>
          <p style={{ fontSize: "13px", color: "var(--text-faint)" }}>{posts.filter(p => p.published).length} published · {posts.filter(p => !p.published).length} drafts</p>
        </div>
        <button onClick={openNew} style={{ padding: "10px 20px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", border: "none", borderRadius: "10px", color: "#121A12", fontWeight: "700", fontSize: "13px", cursor: "pointer" }}>+ New Post</button>
      </div>
      <Alert type={alert?.type} msg={alert?.msg} onClose={() => setAlert(null)} />
      {posts.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", fontSize: "13px" }}>No posts yet. Create your first one.</div>
      ) : (
        <div style={{ display: "grid", gap: "12px" }}>
          {posts.map(post => (
            <div key={post.id} style={{ background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "14px", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                  <span style={{ fontSize: "15px", fontWeight: "700", color: "var(--text-primary)", fontFamily: "'Capsule Sans', sans-serif" }}>{post.title}</span>
                  <span style={{ padding: "2px 10px", borderRadius: "10px", fontSize: "10px", fontFamily: "'JetBrains Mono', monospace", background: post.published ? "rgba(168,216,185,0.12)" : "rgba(255,255,255,0.05)", color: post.published ? "#52D68A" : "#666", border: `1px solid ${post.published ? "rgba(168,216,185,0.2)" : "#1E2C1E"}` }}>
                    {post.published ? "PUBLISHED" : "DRAFT"}
                  </span>
                </div>
                <div style={{ fontSize: "12px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>
                  {new Date(post.updatedAt).toLocaleDateString()} · {(post.tags || []).join(", ") || "no tags"}
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                <button onClick={() => openEdit(post)} style={{ padding: "8px 16px", background: "var(--bg-main)", border: "1px solid var(--border-strong)", borderRadius: "8px", color: "var(--text-secondary)", fontSize: "12px", cursor: "pointer" }}>Edit</button>
                <button onClick={() => del(post)} style={{ padding: "8px 16px", background: "rgba(224,112,112,0.08)", border: "1px solid rgba(224,112,112,0.2)", borderRadius: "8px", color: "#e07070", fontSize: "12px", cursor: "pointer" }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Subscriptions Tab ────────────────────────────────────────────────────────
function SubscriptionsTab({ token }) {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [alert,   setAlert]   = useState(null);

  useEffect(() => {
    fetch(`${API}/api/v1/admin/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setUsers(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const toggleRole = async (user) => {
    const newRole = user.role === "admin" ? "user" : "admin";
    if (!window.confirm(`Make ${user.email} ${newRole === "admin" ? "an admin" : "a regular user"}?`)) return;
    const r = await fetch(`${API}/api/v1/admin/users/${user.id}/role`, {
      method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ role: newRole }),
    });
    if (r.ok) { setAlert({ type: "success", msg: `${user.email} is now ${newRole}.` }); setUsers(us => us.map(u => u.id === user.id ? { ...u, role: newRole } : u)); }
    else setAlert({ type: "error", msg: "Role update failed." });
  };

  const subBadge = (status) => {
    const m = {
      active:   ["rgba(168,216,185,0.12)", "#52D68A"],
      trialing: ["rgba(212,175,55,0.12)", "#D4AF37"],
      past_due: ["rgba(224,180,112,0.12)", "#e0b470"],
      canceled: ["rgba(255,255,255,0.05)", "#666"],
      none:     ["rgba(255,255,255,0.05)", "#555"],
    };
    return m[status] || m.none;
  };

  const filtered = users.filter(u => !search || u.email.includes(search) || (u.name || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={{ marginBottom: "24px" }}>
        <h2 style={{ fontSize: "20px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "4px" }}>Subscribers</h2>
        <p style={{ fontSize: "13px", color: "var(--text-faint)" }}>{users.length} total users</p>
      </div>
      <Alert type={alert?.type} msg={alert?.msg} onClose={() => setAlert(null)} />
      <input
        placeholder="Search by name or email…"
        value={search} onChange={e => setSearch(e.target.value)}
        style={{ width: "100%", maxWidth: "360px", padding: "10px 14px", background: "var(--bg-deepest)", border: "1px solid var(--border-strong)", borderRadius: "10px", color: "var(--text-primary)", fontSize: "13px", fontFamily: "'Capsule Sans', sans-serif", outline: "none", marginBottom: "20px" }}
      />
      {loading ? (
        <div style={{ color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", fontSize: "13px", padding: "40px", textAlign: "center" }}>Loading…</div>
      ) : (
        <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "14px", overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 120px 100px 100px", padding: "12px 20px", borderBottom: "1px solid var(--border-strong)", fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em" }}>
            <span>USER</span><span>EMAIL</span><span>STATUS</span><span>ROLE</span><span style={{ textAlign: "right" }}>ACTION</span>
          </div>
          {filtered.map((u, i) => {
            const [sbg, scolor] = subBadge(u.sub_status);
            return (
              <div key={u.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 120px 100px 100px", padding: "13px 20px", alignItems: "center", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)", borderBottom: i < filtered.length - 1 ? "1px solid var(--border)" : "none" }}>
                <span style={{ fontSize: "13px", color: "var(--text-primary)", fontWeight: "500" }}>{u.name || "—"}</span>
                <span style={{ fontSize: "12px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</span>
                <span style={{ padding: "3px 10px", borderRadius: "10px", fontSize: "10px", background: sbg, color: scolor, fontFamily: "'JetBrains Mono', monospace", display: "inline-block" }}>{(u.sub_status || "none").toUpperCase()}</span>
                <span style={{ fontSize: "12px", color: u.role === "admin" ? "#D4AF37" : "#666", fontFamily: "'JetBrains Mono', monospace" }}>{u.role || "user"}</span>
                <div style={{ textAlign: "right" }}>
                  <button onClick={() => toggleRole(u)} style={{ padding: "5px 12px", background: "transparent", border: "1px solid var(--border-strong)", borderRadius: "7px", color: "var(--text-muted)", fontSize: "11px", cursor: "pointer" }}>
                    {u.role === "admin" ? "Revoke" : "Make Admin"}
                  </button>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div style={{ padding: "40px", textAlign: "center", color: "var(--text-faint)", fontSize: "13px", fontFamily: "'JetBrains Mono', monospace" }}>No users found.</div>}
        </div>
      )}
    </div>
  );
}

// ─── Monitoring Tab ───────────────────────────────────────────────────────────
function MonitoringTab({ token }) {
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/api/v1/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { setStats(await r.json()); setLastRefresh(new Date()); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const fmtUptime = (s) => {
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
    return d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const subTotal = (stats?.subscriptions || []).reduce((s, r) => s + Number(r.count), 0);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "28px" }}>
        <div>
          <h2 style={{ fontSize: "20px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "4px" }}>System Monitoring</h2>
          <p style={{ fontSize: "12px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>Last refreshed {lastRefresh.toLocaleTimeString()}</p>
        </div>
        <button onClick={load} disabled={loading} style={{ padding: "9px 18px", background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "10px", color: "var(--text-secondary)", fontSize: "13px", cursor: "pointer", opacity: loading ? 0.5 : 1 }}>↻ Refresh</button>
      </div>

      {loading || !stats ? (
        <div style={{ color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", fontSize: "13px", padding: "60px", textAlign: "center" }}>Loading stats…</div>
      ) : (
        <>
          {/* System health */}
          <div style={{ marginBottom: "12px", fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em" }}>SERVER HEALTH</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "32px" }}>
            <StatCard label="UPTIME"      value={fmtUptime(stats.system.uptime)} accent />
            <StatCard label="MEMORY USED" value={`${stats.system.memUsedMB} MB`} sub={`${stats.system.memFreeMB} MB free`} />
            <StatCard label="LOAD AVG 1M" value={stats.system.loadAvg1}          sub={stats.system.platform} />
            <StatCard label="NODE VERSION" value={stats.system.nodeVersion}       sub="runtime" />
          </div>

          {/* Users & Blog */}
          <div style={{ marginBottom: "12px", fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em" }}>USERS & CONTENT</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "32px" }}>
            <StatCard label="TOTAL USERS"       value={stats.users.total}        accent />
            <StatCard label="SUBSCRIPTIONS"     value={subTotal}                 sub="all time" />
            <StatCard label="BLOG POSTS"        value={stats.blog.total}         sub={`${stats.blog.published} published`} />
            <StatCard label="DRAFTS"            value={stats.blog.drafts}        sub="unpublished" />
          </div>

          {/* Subscription breakdown */}
          <div style={{ marginBottom: "12px", fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em" }}>SUBSCRIPTION BREAKDOWN</div>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "14px", overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 200px", padding: "12px 20px", borderBottom: "1px solid var(--border-strong)", fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em" }}>
              <span>STATUS</span><span style={{ textAlign: "right" }}>COUNT</span><span style={{ paddingLeft: "16px" }}>SHARE</span>
            </div>
            {(stats.subscriptions || []).map((row, i) => {
              const pct = subTotal ? Math.round(Number(row.count) / subTotal * 100) : 0;
              const colors = { active: "#52D68A", trialing: "#D4AF37", past_due: "#e0b470", canceled: "#666", none: "#444" };
              const c = colors[row.status] || "#555";
              return (
                <div key={row.status} style={{ display: "grid", gridTemplateColumns: "1fr 120px 200px", padding: "13px 20px", alignItems: "center", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)", borderBottom: i < stats.subscriptions.length - 1 ? "1px solid var(--border)" : "none" }}>
                  <span style={{ fontSize: "13px", color: c, fontWeight: "600" }}>{row.status}</span>
                  <span style={{ textAlign: "right", fontSize: "13px", color: "var(--text-primary)", fontFamily: "'JetBrains Mono', monospace" }}>{row.count}</span>
                  <div style={{ paddingLeft: "16px", display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{ flex: 1, height: "6px", background: "var(--bg-hover)", borderRadius: "3px" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: c, borderRadius: "3px", transition: "width 0.5s ease" }} />
                    </div>
                    <span style={{ fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", width: "32px", textAlign: "right" }}>{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Security Tab ─────────────────────────────────────────────────────────────
function SecurityTab({ token }) {
  const EVENTS = [
    { time: "2026-03-14 08:42:11", type: "login",        user: "revanthrondla@gmail.com",   ip: "203.0.113.42",  status: "success" },
    { time: "2026-03-14 07:15:03", type: "login_failed", user: "unknown@test.com",            ip: "198.51.100.7",  status: "blocked" },
    { time: "2026-03-14 06:58:44", type: "login",        user: "revanthrondla@gmail.com",   ip: "203.0.113.42",  status: "success" },
    { time: "2026-03-13 22:30:17", type: "token_refresh",user: "revanthrondla@gmail.com",   ip: "203.0.113.42",  status: "success" },
    { time: "2026-03-13 18:12:55", type: "login_failed", user: "admin@withledger.app",        ip: "192.0.2.100",   status: "blocked" },
    { time: "2026-03-13 15:44:02", type: "login",        user: "admin@withledger.app",        ip: "203.0.113.1",   status: "success" },
    { time: "2026-03-13 11:20:30", type: "logout",       user: "revanthrondla@gmail.com",   ip: "203.0.113.42",  status: "success" },
    { time: "2026-03-12 20:05:18", type: "login",        user: "revanthrondla@gmail.com",   ip: "203.0.113.42",  status: "success" },
  ];

  const EVT_STYLE = {
    login:        { bg: "rgba(168,216,185,0.1)",  color: "#52D68A", label: "LOGIN" },
    login_failed: { bg: "rgba(224,112,112,0.1)",  color: "#e07070", label: "FAILED LOGIN" },
    token_refresh:{ bg: "rgba(212,175,55,0.1)",  color: "#D4AF37", label: "TOKEN REFRESH" },
    logout:       { bg: "rgba(255,255,255,0.05)", color: "var(--text-muted)",    label: "LOGOUT" },
  };

  return (
    <div>
      <div style={{ marginBottom: "24px" }}>
        <h2 style={{ fontSize: "20px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "4px" }}>Security</h2>
        <p style={{ fontSize: "13px", color: "var(--text-faint)" }}>Recent authentication events</p>
      </div>

      {/* Rate limit config */}
      <div style={{ marginBottom: "12px", fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em" }}>RATE LIMIT CONFIG</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", marginBottom: "32px" }}>
        <StatCard label="GLOBAL LIMIT"  value="300 req"  sub="per 15 min window" />
        <StatCard label="LOGIN LIMIT"   value="10 req"   sub="per 15 min window" accent />
        <StatCard label="JWT EXPIRY"    value="15 min"   sub="access token" />
      </div>

      {/* Event log */}
      <div style={{ marginBottom: "12px", fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em" }}>AUTH EVENT LOG</div>
      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "14px", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "160px 140px 1fr 130px 90px", padding: "12px 20px", borderBottom: "1px solid var(--border-strong)", fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em" }}>
          <span>TIMESTAMP</span><span>EVENT</span><span>USER</span><span>IP</span><span style={{ textAlign: "right" }}>STATUS</span>
        </div>
        {EVENTS.map((ev, i) => {
          const s = EVT_STYLE[ev.type] || EVT_STYLE.logout;
          return (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "160px 140px 1fr 130px 90px", padding: "12px 20px", alignItems: "center", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)", borderBottom: i < EVENTS.length - 1 ? "1px solid var(--border)" : "none" }}>
              <span style={{ fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>{ev.time}</span>
              <span style={{ padding: "2px 8px", borderRadius: "6px", fontSize: "10px", background: s.bg, color: s.color, fontFamily: "'JetBrains Mono', monospace", display: "inline-block" }}>{s.label}</span>
              <span style={{ fontSize: "12px", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "12px" }}>{ev.user}</span>
              <span style={{ fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>{ev.ip}</span>
              <span style={{ textAlign: "right", fontSize: "11px", color: ev.status === "success" ? "#52D68A" : "#e07070", fontFamily: "'JetBrains Mono', monospace" }}>{ev.status}</span>
            </div>
          );
        })}
      </div>
      <p style={{ marginTop: "14px", fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>
        NOTE · Full audit logs are stored server-side. Connect a log aggregator (e.g. Datadog, Logtail) for real-time streaming.
      </p>
    </div>
  );
}

// ─── Support Tab ──────────────────────────────────────────────────────────────
const TICKET_STATUS_META = {
  open:        { label: "Open",        bg: "rgba(212,175,55,0.12)", color: "#D4AF37",  border: "rgba(212,175,55,0.3)" },
  in_progress: { label: "In Progress", bg: "rgba(100,160,220,0.12)", color: "#78b4e8",  border: "rgba(100,160,220,0.3)" },
  resolved:    { label: "Resolved",    bg: "rgba(100,200,130,0.12)", color: "#52D68A",  border: "rgba(100,200,130,0.3)" },
  closed:      { label: "Closed",      bg: "rgba(120,120,120,0.1)",  color: "var(--text-muted)",     border: "rgba(120,120,120,0.25)" },
};
const PRIORITY_COLOR = { low: "#555", normal: "#aaa", high: "#D4AF37", urgent: "#e07070" };

function SupportStatusBadge({ status }) {
  const m = TICKET_STATUS_META[status] || TICKET_STATUS_META.open;
  return (
    <span style={{ padding: "3px 10px", borderRadius: "20px", fontSize: "10px", fontWeight: "600",
      letterSpacing: "0.04em", background: m.bg, color: m.color, border: `1px solid ${m.border}`,
      fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap" }}>
      {m.label.toUpperCase()}
    </span>
  );
}

function SupportTab({ token }) {
  const [tickets,    setTickets]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selected,   setSelected]   = useState(null); // ticket id
  const [filter,     setFilter]     = useState("all");
  const [saving,     setSaving]     = useState(false);
  const [alert,      setAlert]      = useState(null);
  // edit state for selected ticket
  const [editStatus, setEditStatus] = useState("");
  const [editNotes,  setEditNotes]  = useState("");

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === "all"
        ? `${API}/api/v1/admin/support`
        : `${API}/api/v1/admin/support?status=${filter}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setTickets(await r.json());
    } catch {}
    setLoading(false);
  }, [token, filter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const openTicket = (t) => {
    setSelected(t.id === selected ? null : t.id);
    setEditStatus(t.status);
    setEditNotes(t.admin_notes || "");
    setAlert(null);
  };

  const saveTicket = async (id) => {
    setSaving(true); setAlert(null);
    try {
      const r = await fetch(`${API}/api/v1/admin/support/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: editStatus, admin_notes: editNotes }),
      });
      if (r.ok) {
        setAlert({ type: "success", msg: "Ticket updated." });
        fetchTickets();
      } else {
        setAlert({ type: "error", msg: "Failed to update." });
      }
    } catch { setAlert({ type: "error", msg: "Connection error." }); }
    setSaving(false);
  };

  const STATUS_FILTERS = ["all", "open", "in_progress", "resolved", "closed"];
  const openCount = tickets.filter(t => t.status === "open").length;

  const inpStyle = {
    width: "100%", padding: "10px 14px",
    background: "var(--bg-deepest)", border: "1px solid var(--border-strong)",
    borderRadius: "9px", color: "var(--text-primary)", fontSize: "13px",
    fontFamily: "'Capsule Sans', sans-serif", outline: "none", boxSizing: "border-box",
  };

  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h2 style={{ fontSize: "18px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "4px" }}>Support Requests</h2>
          <p style={{ fontSize: "12px", color: "var(--text-faint)" }}>
            {openCount > 0 ? `${openCount} open ticket${openCount > 1 ? "s" : ""} needing attention` : "All tickets up to date"}
          </p>
        </div>
        <button onClick={fetchTickets} style={{ background: "none", border: "1px solid var(--border-strong)", borderRadius: "8px", color: "var(--text-muted)", fontSize: "12px", padding: "7px 14px", cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif" }}>
          ↻ Refresh
        </button>
      </div>

      <Alert type={alert?.type} msg={alert?.msg} onClose={() => setAlert(null)} />

      {/* Status filter strip */}
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "20px" }}>
        {STATUS_FILTERS.map(f => (
          <button key={f} onClick={() => { setFilter(f); setSelected(null); }}
            style={{
              padding: "6px 14px", borderRadius: "8px", border: "none",
              background: filter === f ? "rgba(212,175,55,0.15)" : "var(--bg-card)",
              color: filter === f ? "#D4AF37" : "#666",
              fontSize: "12px", cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif",
              outline: filter === f ? "1px solid rgba(212,175,55,0.3)" : "1px solid var(--border-strong)",
              fontWeight: filter === f ? "600" : "400",
            }}>
            {f === "all" ? "All" : TICKET_STATUS_META[f]?.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", fontSize: "11px" }}>LOADING…</div>
      ) : tickets.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px", background: "var(--bg-main)", borderRadius: "14px", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: "13px", color: "var(--text-faint)" }}>No tickets found.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {tickets.map(t => {
            const isOpen = selected === t.id;
            return (
              <div key={t.id} style={{
                background: "var(--bg-main)", border: `1px solid ${isOpen ? "#243424" : "var(--border)"}`,
                borderRadius: "14px", overflow: "hidden", transition: "border-color 0.2s",
              }}>
                {/* Row */}
                <div
                  onClick={() => openTicket(t)}
                  style={{ padding: "16px 20px", cursor: "pointer", display: "grid",
                    gridTemplateColumns: "1fr auto auto auto", gap: "12px", alignItems: "center" }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px", flexWrap: "wrap" }}>
                      <span style={{ fontSize: "10px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>#{t.id}</span>
                      <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-primary)" }}>{t.subject}</span>
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                      {t.user_name} · {t.user_email} ·{" "}
                      <span style={{ color: PRIORITY_COLOR[t.priority] || "#aaa" }}>{t.priority}</span> ·{" "}
                      {t.category} ·{" "}
                      {new Date(t.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </div>
                  </div>
                  <SupportStatusBadge status={t.status} />
                  <span style={{ color: "var(--text-faint)", fontSize: "12px", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
                </div>

                {/* Expanded panel */}
                {isOpen && (
                  <div style={{ borderTop: "1px solid var(--border)", padding: "20px 20px 24px" }}>
                    <div style={{ marginBottom: "16px" }}>
                      <div style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "8px" }}>USER MESSAGE</div>
                      <div style={{ fontSize: "13px", color: "var(--text-secondary)", lineHeight: "1.7", whiteSpace: "pre-wrap", background: "var(--bg-deepest)", borderRadius: "10px", padding: "14px 16px", border: "1px solid var(--border)" }}>
                        {t.description}
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                      {/* Status */}
                      <div>
                        <label style={{ display: "block", fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "8px" }}>UPDATE STATUS</label>
                        <select
                          value={editStatus}
                          onChange={e => setEditStatus(e.target.value)}
                          style={{ ...inpStyle }}
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                      {/* Timestamps */}
                      <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                        <div style={{ fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", lineHeight: "1.8" }}>
                          <div>Created: {new Date(t.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                          <div>Updated: {new Date(t.updated_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                        </div>
                      </div>
                    </div>

                    {/* Admin notes */}
                    <div style={{ marginBottom: "16px" }}>
                      <label style={{ display: "block", fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "8px" }}>RESPONSE / NOTES (VISIBLE TO USER)</label>
                      <textarea
                        rows={4}
                        value={editNotes}
                        onChange={e => setEditNotes(e.target.value)}
                        placeholder="Write a response that the user will see when they view their ticket…"
                        style={{ ...inpStyle, resize: "vertical", lineHeight: "1.6" }}
                      />
                    </div>

                    <button
                      onClick={() => saveTicket(t.id)}
                      disabled={saving}
                      style={{
                        padding: "10px 24px",
                        background: saving ? "#333" : "linear-gradient(135deg, #D4AF37, #9A7C1E)",
                        border: "none", borderRadius: "9px",
                        color: saving ? "#666" : "#121A12",
                        fontSize: "13px", fontWeight: "700", cursor: saving ? "not-allowed" : "pointer",
                        fontFamily: "'Capsule Sans', sans-serif", opacity: saving ? 0.6 : 1,
                      }}
                    >
                      {saving ? "Saving…" : "Save Changes →"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Admin Dashboard ──────────────────────────────────────────────────────────
export default function AdminDashboard({ token }) {
  const [tab,        setTab]        = useState("blog");
  const [openTickets, setOpenTickets] = useState(0);

  // Fetch open ticket count for badge
  useEffect(() => {
    fetch(`${API}/api/v1/admin/support?status=open`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(d => setOpenTickets(d.length))
      .catch(() => {});
  }, [token]);

  const TABS = [
    { id: "blog",          label: "Blog",          icon: "✦" },
    { id: "subscriptions", label: "Subscriptions", icon: "◉" },
    { id: "support",       label: "Support",       icon: "◎", badge: openTickets || undefined },
    { id: "security",      label: "Security",      icon: "⊞" },
    { id: "monitoring",    label: "Monitoring",    icon: "△" },
  ];

  return (
    <div style={{ display: "flex", minHeight: "100%", fontFamily: "'Capsule Sans', sans-serif" }}>
      {/* Sidebar */}
      <div style={{ width: "200px", flexShrink: 0, borderRight: "1px solid var(--border)", padding: "32px 16px" }}>
        <div style={{ fontSize: "10px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.14em", marginBottom: "16px", paddingLeft: "12px" }}>ADMIN PANEL</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {TABS.map(t => <TabBtn key={t.id} label={t.label} active={tab === t.id} onClick={() => setTab(t.id)} badge={t.badge} />)}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: "32px 40px", overflowY: "auto", minWidth: 0 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "32px" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>⚙</div>
          <div>
            <h1 style={{ fontSize: "22px", fontWeight: "800", color: "var(--text-primary)", letterSpacing: "-0.03em" }}>Admin Dashboard</h1>
            <p style={{ fontSize: "12px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>withledger.app · Manage your platform</p>
          </div>
        </div>

        {tab === "blog"          && <BlogTab          token={token} />}
        {tab === "subscriptions" && <SubscriptionsTab token={token} />}
        {tab === "support"       && <SupportTab       token={token} />}
        {tab === "security"      && <SecurityTab      token={token} />}
        {tab === "monitoring"    && <MonitoringTab    token={token} />}
      </div>
    </div>
  );
}
